"use client";

import * as React from "react";

import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

import { ActionBar } from "../action-bar";
import { VS_FONT_FEATURES } from "../constants";
import { ACTION_BAR_TEXT } from "../data";
import { VsGrainOverlay } from "../grain-overlay";
import { VsMeshGradient } from "../mesh-gradient";
import { VsNav } from "../nav";

// ——— HubSpot config ———

const PORTAL_ID = "6440909";
const FORM_GUID = "c816e208-4be4-4580-8cad-4ee0cd4f14b9";

// ——— Types ———

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  brandName: string;
  website: string;
  category: string;
  revenue: string;
  salesChannels: string[];
  pitch: string;
  videoUrl: string;
  source: string;
  consent: boolean;
}

type FieldErrors = Partial<Record<keyof FormValues, string>>;

const EMPTY_VALUES: FormValues = {
  firstName: "",
  lastName: "",
  email: "",
  role: "",
  brandName: "",
  website: "",
  category: "",
  revenue: "",
  salesChannels: [],
  pitch: "",
  videoUrl: "",
  source: "",
  consent: false,
};

// ——— Validation ———

function validateStep(step: number, v: FormValues): FieldErrors {
  const e: FieldErrors = {};
  if (step === 1) {
    if (!v.firstName.trim()) e.firstName = "Please enter your first name";
    if (!v.lastName.trim()) e.lastName = "Please enter your last name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email))
      e.email = "Enter a valid email address";
    if (!v.role) e.role = "Please select your role";
  }
  if (step === 2) {
    if (!v.brandName.trim()) e.brandName = "Please enter your brand name";
    if (!/^https?:\/\/.+\..+/.test(v.website))
      e.website = "Enter a valid URL starting with https://";
    if (!v.category) e.category = "Please select a category";
    if (!v.revenue) e.revenue = "Please select a revenue range";
    if (v.salesChannels.length === 0)
      e.salesChannels = "Please select at least one channel";
  }
  if (step === 3) {
    const wordCount = v.pitch.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount < 20)
      e.pitch = "Please tell us about your brand (minimum 20 words)";
    if (!/^https?:\/\/.+\..+/.test(v.videoUrl))
      e.videoUrl = "Please include a valid video link";
  }
  if (step === 4) {
    if (!v.consent) e.consent = "Please accept the terms to continue";
  }
  return e;
}

// ——— Shared input helpers ———

function inputCn(hasError?: boolean) {
  return cn(
    "w-full rounded-[8px] border bg-white/[0.05] px-4 py-[13px] text-[14px] leading-[1.4] text-white transition-colors duration-150 outline-none placeholder:text-white/25",
    "focus:bg-white/[0.07] focus:ring-[3px]",
    hasError
      ? "border-[#ff6060] focus:border-[#ff6060] focus:ring-[#ff6060]/15"
      : "border-white/[0.12] focus:border-[#001eff] focus:ring-[#001eff]/20"
  );
}

interface FieldProps {
  label: React.ReactNode;
  hint?: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
  /** Use for checkbox/radio/toggle groups instead of a single labelled control. */
  asGroup?: boolean;
}

function FormField({
  label,
  hint,
  error,
  children,
  className,
  asGroup = false,
}: FieldProps) {
  const fieldId = React.useId();
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  const child = React.Children.only(children);
  const enhancedChild =
    !asGroup && React.isValidElement(child)
      ? React.cloneElement(
          child as React.ReactElement<Record<string, unknown>>,
          {
            id: fieldId,
            ...(describedBy ? { "aria-describedby": describedBy } : {}),
            ...(error ? { "aria-invalid": true } : {}),
          }
        )
      : children;

  const Wrapper = asGroup ? "fieldset" : "div";

  return (
    <Wrapper
      className={cn(
        "flex flex-col gap-[7px]",
        asGroup && "m-0 border-0 p-0",
        className
      )}
      {...(asGroup && errorId ? { "aria-describedby": errorId } : {})}
    >
      {asGroup ? (
        <legend className="text-[13px] leading-none font-medium text-white">
          {label}
        </legend>
      ) : (
        <label
          htmlFor={fieldId}
          className="text-[13px] leading-none font-medium text-white"
        >
          {label}
        </label>
      )}
      {enhancedChild}
      {hint && !error && (
        <p id={hintId} className="text-[12px] leading-[1.4] text-white/35">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-[12px] leading-[1.4] text-[#ff6060]">
          {error}
        </p>
      )}
    </Wrapper>
  );
}

interface SelectWrapProps {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  hasError?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  children: React.ReactNode;
}

function SelectWrap({
  id,
  value,
  onChange,
  hasError,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  children,
}: SelectWrapProps) {
  return (
    <div className="relative">
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid ?? (hasError ? true : undefined)}
        style={{ colorScheme: "dark" }}
        className={cn(
          inputCn(hasError),
          "cursor-pointer appearance-none pr-10"
        )}
      >
        {children}
      </select>
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2 text-white/35"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
    </div>
  );
}

// ——— Step header ———

function StepHeader({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="mb-9">
      <h2 className="mb-2 text-[36px] leading-[1.1] font-normal tracking-[-0.04em] text-white">
        {title}
      </h2>
      <p className="text-[15px] leading-[1.55] text-white/55">{desc}</p>
    </div>
  );
}

// ——— Button row ———

function NavButtons({
  onNext,
  onBack,
  nextLabel = "Continue",
  submitting,
}: {
  onNext?: () => void;
  onBack?: () => void;
  nextLabel?: string;
  submitting?: boolean;
}) {
  return (
    <div className="mt-10 flex items-center justify-between gap-4">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="text-[14px] font-medium text-white/40 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]"
        >
          ← Back
        </button>
      ) : (
        <div />
      )}
      <button
        type="button"
        onClick={onNext}
        disabled={submitting}
        className={cn(
          "inline-flex items-center gap-2 rounded-full bg-[#001eff] px-6 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-[#0018cc] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d] motion-reduce:transition-none",
          submitting && "cursor-not-allowed opacity-60"
        )}
      >
        {submitting ? (
          <>
            <span
              aria-hidden
              className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white"
            />
            Submitting…
          </>
        ) : (
          <>
            {nextLabel}
            <span aria-hidden>→</span>
          </>
        )}
      </button>
    </div>
  );
}

// ——— Step 1: About you ———

function Step1({
  values,
  errors,
  set,
  onNext,
}: {
  values: FormValues;
  errors: FieldErrors;
  set: <K extends keyof FormValues>(k: K, v: FormValues[K]) => void;
  onNext: () => void;
}) {
  return (
    <div>
      <StepHeader title="About you" desc="Tell us who's behind the brand." />
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
          <FormField label="First name" error={errors.firstName}>
            <input
              type="text"
              placeholder="Jane"
              autoComplete="on"
              value={values.firstName}
              onChange={(e) => set("firstName", e.target.value)}
              className={inputCn(!!errors.firstName)}
            />
          </FormField>
          <FormField label="Last name" error={errors.lastName}>
            <input
              type="text"
              placeholder="Smith"
              autoComplete="on"
              value={values.lastName}
              onChange={(e) => set("lastName", e.target.value)}
              className={inputCn(!!errors.lastName)}
            />
          </FormField>
        </div>
        <FormField label="Email address" error={errors.email}>
          <input
            type="email"
            placeholder="jane@yourbrand.com"
            autoComplete="email"
            value={values.email}
            onChange={(e) => set("email", e.target.value)}
            className={inputCn(!!errors.email)}
          />
        </FormField>
        <FormField label="Your role" error={errors.role}>
          <SelectWrap
            value={values.role}
            onChange={(v) => set("role", v)}
            hasError={!!errors.role}
          >
            <option value="" disabled>
              Select your role
            </option>
            <option>Founder / Co-founder</option>
            <option>CEO</option>
            <option>COO</option>
            <option>CMO</option>
            <option>Head of Growth</option>
            <option>Other</option>
          </SelectWrap>
        </FormField>
      </div>
      <NavButtons onNext={onNext} />
    </div>
  );
}

// ——— Step 2: Your brand ———

const SALES_CHANNELS = [
  "Own website",
  "Amazon",
  "Retail / wholesale",
  "Social commerce",
  "Subscription",
  "Pop-ups / markets",
] as const;

function Step2({
  values,
  errors,
  set,
  toggleChannel,
  onNext,
  onBack,
}: {
  values: FormValues;
  errors: FieldErrors;
  set: <K extends keyof FormValues>(k: K, v: FormValues[K]) => void;
  toggleChannel: (channel: string) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  return (
    <div>
      <StepHeader
        title="Your brand"
        desc="Help us understand what you're building."
      />
      <div className="flex flex-col gap-5">
        <FormField label="Brand name" error={errors.brandName}>
          <input
            type="text"
            placeholder="e.g. Wild Deodorant"
            value={values.brandName}
            onChange={(e) => set("brandName", e.target.value)}
            className={inputCn(!!errors.brandName)}
          />
        </FormField>
        <FormField
          label="Website"
          hint="Must be an active eCommerce site"
          error={errors.website}
        >
          <input
            type="url"
            placeholder="https://yourbrand.com"
            value={values.website}
            onChange={(e) => set("website", e.target.value)}
            className={inputCn(!!errors.website)}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
          <FormField label="Category" error={errors.category}>
            <SelectWrap
              value={values.category}
              onChange={(v) => set("category", v)}
              hasError={!!errors.category}
            >
              <option value="" disabled>
                Select category
              </option>
              <option>Fashion &amp; Apparel</option>
              <option>Beauty &amp; Skincare</option>
              <option>Food &amp; Beverage</option>
              <option>Health &amp; Supplements</option>
              <option>Homeware &amp; Lifestyle</option>
              <option>Sports &amp; Fitness</option>
              <option>Baby &amp; Kids</option>
              <option>Pet Products</option>
              <option>Technology &amp; Accessories</option>
              <option>Other</option>
            </SelectWrap>
          </FormField>
          <FormField label="Monthly revenue" error={errors.revenue}>
            <SelectWrap
              value={values.revenue}
              onChange={(v) => set("revenue", v)}
              hasError={!!errors.revenue}
            >
              <option value="" disabled>
                Select range
              </option>
              <option>Pre-revenue</option>
              <option>Under £5k/month</option>
              <option>£5k – £25k/month</option>
              <option>£25k – £100k/month</option>
              <option>£100k – £500k/month</option>
              <option>Over £500k/month</option>
              <option>Prefer not to say</option>
            </SelectWrap>
          </FormField>
        </div>
        <p className="-mt-2 text-[12px] text-white/30">
          We encourage brands under £100k/month to apply.
        </p>
        <FormField
          asGroup
          label="Where do you sell?"
          error={errors.salesChannels}
        >
          <div className="flex flex-wrap gap-2">
            {SALES_CHANNELS.map((channel) => {
              const checked = values.salesChannels.includes(channel);
              return (
                <button
                  key={channel}
                  type="button"
                  onClick={() => toggleChannel(channel)}
                  aria-pressed={checked}
                  className={cn(
                    "rounded-full border px-4 py-2.5 text-[13px] font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d] motion-reduce:transition-none",
                    checked
                      ? "border-[#001eff] bg-[#001eff]/10 text-[#001eff]"
                      : "border-white/10 text-white/55 hover:border-white/25 hover:text-white/80"
                  )}
                >
                  {channel}
                </button>
              );
            })}
          </div>
        </FormField>
      </div>
      <NavButtons onNext={onNext} onBack={onBack} />
    </div>
  );
}

// ——— Step 3: Your pitch ———

function Step3({
  values,
  errors,
  set,
  onNext,
  onBack,
}: {
  values: FormValues;
  errors: FieldErrors;
  set: <K extends keyof FormValues>(k: K, v: FormValues[K]) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  return (
    <div>
      <StepHeader
        title="Your pitch"
        desc="What makes you a serious operator?"
      />
      <div className="flex flex-col gap-5">
        <FormField
          label="Tell us about your brand and what £10,000 would unlock"
          hint="Aim for 100–300 words. We read every entry personally."
          error={errors.pitch}
        >
          <textarea
            rows={7}
            placeholder="Tell us who you are, what you're building, and what £10,000 would unlock for your brand. No need to be formal — write how you talk."
            value={values.pitch}
            onChange={(e) => set("pitch", e.target.value)}
            className={cn(inputCn(!!errors.pitch), "resize-y")}
          />
        </FormField>
        <FormField
          label={
            <>
              Video pitch link{" "}
              <span className="font-normal text-white/30">
                — Loom · YouTube unlisted · Google Drive
              </span>
            </>
          }
          hint="60 seconds max. Film on your phone - it doesn't need to be polished."
          error={errors.videoUrl}
        >
          <input
            type="url"
            placeholder="https://loom.com/share/..."
            value={values.videoUrl}
            onChange={(e) => set("videoUrl", e.target.value)}
            className={inputCn(!!errors.videoUrl)}
          />
        </FormField>
      </div>
      <NavButtons onNext={onNext} onBack={onBack} />
    </div>
  );
}

// ——— Step 4: Final details ———

function Step4({
  values,
  errors,
  set,
  onSubmit,
  onBack,
  submitting,
}: {
  values: FormValues;
  errors: FieldErrors;
  set: <K extends keyof FormValues>(k: K, v: FormValues[K]) => void;
  onSubmit: () => void;
  onBack: () => void;
  submitting: boolean;
}) {
  return (
    <div>
      <StepHeader
        title="Almost there"
        desc="One last thing before we submit your application."
      />
      <div className="flex flex-col gap-5">
        <FormField
          label={
            <>
              How did you hear about Pitch Night?{" "}
              <span className="font-normal text-white/30">Optional</span>
            </>
          }
        >
          <SelectWrap value={values.source} onChange={(v) => set("source", v)}>
            <option value="" disabled>
              Select an option
            </option>
            <option>Wayflyer email</option>
            <option>LinkedIn</option>
            <option>Instagram</option>
            <option>Friend or colleague</option>
            <option>Speaker or event</option>
            <option>Wayflyer team member</option>
            <option>Other</option>
          </SelectWrap>
        </FormField>

        <div className="flex flex-col gap-2">
          <label
            className={cn(
              "flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors",
              errors.consent
                ? "border-[#ff6060] bg-[#ff6060]/5"
                : "border-white/[0.08] bg-white/[0.03] hover:border-white/15"
            )}
          >
            <input
              type="checkbox"
              checked={values.consent}
              onChange={(e) => set("consent", e.target.checked)}
              className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer accent-[#001eff]"
            />
            <span className="text-[13px] leading-[1.55] text-white/55">
              I confirm I am applying on behalf of a UK-based ecommerce brand
              with an active website. I agree to Wayflyer&apos;s{" "}
              <Link
                href="/privacy-notice"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-white"
              >
                Privacy Policy
              </Link>{" "}
              and understand my application may be shared internally for judging
              purposes.
            </span>
          </label>
          {errors.consent && (
            <p className="text-[12px] text-[#ff6060]">{errors.consent}</p>
          )}
        </div>
      </div>

      <NavButtons
        onNext={onSubmit}
        onBack={onBack}
        nextLabel="Submit application"
        submitting={submitting}
      />

      <p className="mt-6 text-center text-[12px] text-white/30">
        We&apos;ll confirm receipt by email within a few minutes.
      </p>
    </div>
  );
}

// ——— Success screen ———

function SuccessScreen() {
  return (
    <div className="py-10 text-center">
      <div className="mx-auto mb-6 flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#001eff]">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h2 className="mb-3 text-[36px] font-normal tracking-[-0.04em] text-white">
        Application received.
      </h2>
      <p className="mx-auto mb-8 max-w-[360px] text-[15px] leading-[1.75] text-white/55">
        We&apos;ve received your application and will be in touch. We read every
        entry personally — if yours is strong, you&apos;ll hear from us
        directly.
      </p>
      <Link
        href="/visionaries-summit"
        className="inline-flex items-center gap-1.5 rounded-full bg-[#001eff] px-6 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-[#0018cc] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d] motion-reduce:transition-none"
      >
        Back to Visionaries Summit
        <span aria-hidden>→</span>
      </Link>
    </div>
  );
}

// ——— Left info panel ———

const FACTS = [
  {
    title: "Upload your 60-second video",
    body: "Tell us who you are, what you're building, and what £10,000 would unlock.",
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
  },
  {
    title: "Six finalists, live pitches",
    body: "Top 6 pitch in person at the Wayflyer Visionaries Summit, Batch LDN, June 5th.",
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    title: "£10,000 cash prize",
    body: "One founder will walk away with the cash on the night.",
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
        <path d="M4 22h16" />
        <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
        <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
        <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
      </svg>
    ),
  },
  {
    title: "Open to any UK eCommerce brand",
    body: "Not just Wayflyer customers. Doing £100k per month or less.",
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
  },
] as const;

export function PitchNightPanelContent({ className }: { className?: string }) {
  return (
    <div className={cn("flex flex-col gap-5", className)}>
      {FACTS.map((fact) => (
        <div key={fact.title} className="flex items-start gap-3.5">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[6px] border border-white/[0.1] bg-white/[0.05] text-white/50">
            {fact.icon}
          </div>
          <div>
            <p className="mb-0.5 text-[13px] font-medium text-white">
              {fact.title}
            </p>
            <p className="text-[13px] leading-[1.5] text-[#95999d]">
              {fact.body}
            </p>
          </div>
        </div>
      ))}
      <div className="mt-4 border-t border-white/[0.08] pt-5 text-[11px] font-medium tracking-[0.0967em] text-[#95999d] uppercase">
        Applications close May 29th 2026.
      </div>
    </div>
  );
}

function LeftPanel() {
  return (
    <aside
      // oxlint-disable-next-line eslint-plugin-jsx-a11y(no-noninteractive-tabindex) -- scrollable aside needs keyboard access (axe scrollable-region-focusable)
      tabIndex={0}
      className="sticky top-[96px] hidden h-[calc(100vh-96px)] w-[420px] flex-shrink-0 flex-col overflow-y-auto px-12 py-14 lg:flex"
      aria-label="Pitch Night details"
    >
      <p className="mb-5 text-[14px] font-normal text-white/60">
        Wayflyer Presents
      </p>

      <h1 className="mb-5 text-[56px] leading-[1.0] font-normal tracking-[-0.04em] text-white">
        Pitch Night.
      </h1>

      <p className="mb-10 max-w-[300px] text-[16px] leading-[1.5] text-white/55">
        £10,000 for the UK&apos;s most exciting consumer brand.
      </p>

      <PitchNightPanelContent className="mt-auto" />
    </aside>
  );
}

// ——— Main exported component ———

// ——— Embeddable form card (used in BigPitchHero and standalone apply page) ———

export function PitchNightApplyCard({ className }: { className?: string }) {
  const [step, setStep] = React.useState(1);
  const [values, setValues] = React.useState<FormValues>(EMPTY_VALUES);
  const [errors, setErrors] = React.useState<FieldErrors>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const panelRef = React.useRef<HTMLDivElement>(null);

  function set<K extends keyof FormValues>(key: K, val: FormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function toggleChannel(channel: string) {
    setValues((prev) => ({
      ...prev,
      salesChannels: prev.salesChannels.includes(channel)
        ? prev.salesChannels.filter((c) => c !== channel)
        : [...prev.salesChannels, channel],
    }));
    setErrors((prev) => ({ ...prev, salesChannels: undefined }));
  }

  function scrollToTop() {
    panelRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goNext() {
    const errs = validateStep(step, values);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStep((s) => s + 1);
    scrollToTop();
  }

  function goBack() {
    setStep((s) => s - 1);
    scrollToTop();
  }

  async function handleSubmit() {
    const errs = validateStep(4, values);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${PORTAL_ID}/${FORM_GUID}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fields: [
              { name: "firstname", value: values.firstName },
              { name: "lastname", value: values.lastName },
              { name: "email", value: values.email },
              { name: "jobtitle", value: values.role },
              { name: "company", value: values.brandName },
              { name: "website", value: values.website },
              { name: "industry", value: values.category },
              { name: "monthly_revenue", value: values.revenue },
              {
                name: "sales_channels",
                value: values.salesChannels.join(", "),
              },
              { name: "pitch_description", value: values.pitch },
              { name: "video_pitch_url", value: values.videoUrl },
              { name: "lead_source", value: values.source || "Not specified" },
            ],
            context: {
              pageUri:
                typeof window !== "undefined" ? window.location.href : "",
              pageName: "Pitch Night Application",
            },
          }),
        }
      );
      if (res.ok) {
        setSubmitted(true);
        scrollToTop();
      } else {
        alert(
          "Something went wrong. Please try again or email visionaries@wayflyer.com"
        );
      }
    } catch {
      alert(
        "Something went wrong. Please try again or email visionaries@wayflyer.com"
      );
    } finally {
      setSubmitting(false);
    }
  }

  const progressPct = `${step * 25}%`;

  return (
    <div
      ref={panelRef}
      style={{ fontFeatureSettings: VS_FONT_FEATURES }}
      className={cn("overflow-y-auto", className)}
    >
      {submitted ? (
        <SuccessScreen />
      ) : (
        <>
          {/* Progress bar */}
          <div className="mb-12">
            <div className="h-[2px] w-full overflow-hidden rounded-full bg-white/[0.08]">
              <div
                className="h-full rounded-full transition-[width] duration-500 ease-in-out motion-reduce:transition-none"
                style={{
                  width: progressPct,
                  background:
                    "linear-gradient(90deg,#001eff 0%,#7623f9 50%,#fe5960 100%)",
                }}
              />
            </div>
          </div>

          {step === 1 && (
            <Step1 values={values} errors={errors} set={set} onNext={goNext} />
          )}
          {step === 2 && (
            <Step2
              values={values}
              errors={errors}
              set={set}
              toggleChannel={toggleChannel}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {step === 3 && (
            <Step3
              values={values}
              errors={errors}
              set={set}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {step === 4 && (
            <Step4
              values={values}
              errors={errors}
              set={set}
              onSubmit={handleSubmit}
              onBack={goBack}
              submitting={submitting}
            />
          )}
        </>
      )}
    </div>
  );
}

// ——— Main exported page component ———

export function PitchNightApplyForm() {
  return (
    <div
      style={{ fontFeatureSettings: VS_FONT_FEATURES }}
      className="relative overflow-hidden bg-[#3a1660] [--vs-action-h:72px] md:[--vs-action-h:36px]"
    >
      <VsMeshGradient />
      <VsGrainOverlay />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 bottom-0"
        style={{
          background:
            "linear-gradient(to bottom, transparent 0%, rgba(13,13,13,0.55) 20%, #0d0d0d 45%)",
        }}
      />

      <ActionBar text={ACTION_BAR_TEXT} />
      <VsNav applyHref="/visionaries-summit" applyLabel="View the event" />

      <div className="relative z-[1] flex min-h-screen pt-[132px] md:pt-[96px]">
        <LeftPanel />

        <main className="flex-1 px-6 py-10 md:px-12 lg:px-[72px] lg:py-16">
          <div className="mx-auto max-w-[560px]">
            {/* Mobile event summary */}
            <div className="mb-10 lg:hidden">
              <p className="mb-3 text-[14px] font-normal text-white/60">
                Wayflyer Presents
              </p>
              <h1 className="text-[36px] leading-[1.0] font-normal tracking-[-0.04em] text-white">
                Pitch Night.
              </h1>
              <p className="mt-2 text-[14px] leading-[1.65] text-white/50">
                £10,000 for the UK&apos;s most exciting consumer brand.
              </p>
            </div>

            <PitchNightApplyCard />
          </div>
        </main>
      </div>
    </div>
  );
}
